import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { cn } from '@/utils/cnUtils';
import { formatInLanguage } from '@/utils/dateLocale';
import { JournalEntryLink } from '@/components/journalEntryLink';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    DISCREPANCY_CLASS,
    NO_FIGURE,
    SHIFT_STATUS_CHIP,
    formatTetriOrDash,
    type ShiftRowModel,
} from '../../shiftsMeta';

type ShiftTableProps = {
    rows: ShiftRowModel[];
    onSelect: (shiftId: string) => void;
    /**
     * The register is still in flight. The grid keeps its real column headers
     * and skeletons only the rows — an empty list while loading means "not known
     * yet", never "no shifts".
     */
    isLoading: boolean;
};

const SKELETON_ROW_COUNT = 5;

const TIMESTAMP_FORMAT = 'DD/MM/YYYY HH:mm';

/**
 * The register of shifts.
 *
 * A HeroUI Pro `DataGrid`, like every other list screen in the app: a real
 * `<table>` with genuine `columnheader`s and a `rowheader` on the shift, so it
 * reads as a listing rather than a grid of boxes. Activating a row opens the
 * rollup drawer — the journal register's idiom, and the same gesture an
 * accountant already uses to open an entry.
 *
 * **The discrepancy column distinguishes three states, not two.** An open shift
 * has no discrepancy yet, which is not the same fact as a shift that closed
 * exactly on the count; rendering both as `₾ 0.00` would claim a drawer had been
 * reconciled while the till is still taking money. The dash is the difference.
 */
export const ShiftTable: React.FC<ShiftTableProps> = ({
    rows,
    onSelect,
    isLoading,
}) => {
    const { t, i18n } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<ShiftRowModel>[]>(
        () => [
            {
                id: 'opened',
                header: t('branches.shifts.columns.opened'),
                isRowHeader: true,
                minWidth: 190,
                cell: (row) => (
                    <span className="flex flex-col">
                        <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                            {formatInLanguage(
                                row.openedAt,
                                TIMESTAMP_FORMAT,
                                i18n.language,
                            )}
                        </span>
                        <span className="whitespace-nowrap text-xs text-muted">
                            {row.openedByName ?? row.openedBySellerId}
                        </span>
                    </span>
                ),
            },
            {
                id: 'closed',
                header: t('branches.shifts.columns.closed'),
                minWidth: 190,
                cell: (row) => (
                    <span className="flex flex-col">
                        <span className="whitespace-nowrap tabular-nums text-foreground">
                            {row.closedAt
                                ? formatInLanguage(
                                      row.closedAt,
                                      TIMESTAMP_FORMAT,
                                      i18n.language,
                                  )
                                : NO_FIGURE}
                        </span>
                        <span className="whitespace-nowrap text-xs text-muted">
                            {row.closedByName ?? ''}
                        </span>
                    </span>
                ),
            },
            {
                id: 'branch',
                header: t('branches.shifts.columns.branch'),
                minWidth: 140,
                cell: (row) => (
                    <span className="text-foreground">{row.branchName}</span>
                ),
            },
            {
                id: 'status',
                header: t('branches.shifts.columns.status'),
                minWidth: 110,
                cell: (row) => {
                    const chip = SHIFT_STATUS_CHIP[row.status];
                    return (
                        <div className="flex flex-col items-start gap-1">
                            <span
                                className={cn(
                                    'inline-flex w-fit items-center rounded-lg px-2.5 py-1 text-xs font-medium',
                                    chip.className,
                                )}
                                {...getNodeTestIdProp(
                                    `branches-shifts-status-${row.id}`,
                                )}
                            >
                                {t(chip.labelKey)}
                            </span>
                            {/* BOS-167: a drawer is opened at the start of a
                                working day and counted down at the end of it.
                                One still open a day later is not a busy shift —
                                it is one nobody closed, and every sale since has
                                been attributed to it. */}
                            {row.isStale ? (
                                <span
                                    className="inline-flex w-fit items-center rounded-lg bg-warning-soft px-2.5 py-1 text-xs font-medium text-warning-soft-foreground"
                                    {...getNodeTestIdProp(
                                        `branches-shifts-stale-${row.id}`,
                                    )}
                                >
                                    {t('branches.shifts.staleChip', {
                                        count: row.openForDays,
                                    })}
                                </span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                id: 'receipts',
                header: t('branches.shifts.columns.receipts'),
                align: 'end',
                minWidth: 110,
                cell: (row) => (
                    <span className="tabular-nums text-foreground">
                        {row.receiptCount}
                    </span>
                ),
            },
            {
                id: 'counted',
                header: t('branches.shifts.columns.counted'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums text-foreground">
                        {formatTetriOrDash(row.closingCountTetri)}
                    </span>
                ),
            },
            {
                id: 'discrepancy',
                header: t('branches.shifts.columns.discrepancy'),
                align: 'end',
                minWidth: 130,
                // BOS-164: the figure and the entry that booked it, together.
                // A difference the ledger never heard about renders the figure
                // alone — which is what a shift closed before the posting
                // existed looks like, and saying so beats a dead link.
                cell: (row) => (
                    <div className="flex flex-col items-end gap-0.5">
                        <span
                            className={cn(
                                'whitespace-nowrap font-medium tabular-nums',
                                DISCREPANCY_CLASS[row.discrepancyTone],
                            )}
                            {...getNodeTestIdProp(
                                `branches-shifts-discrepancy-${row.id}`,
                            )}
                        >
                            {formatTetriOrDash(row.closeDiscrepancyTetri)}
                        </span>
                        <JournalEntryLink
                            date={row.closedAt ?? row.openedAt}
                            entryId={row.closeJournalEntryId}
                            slot={`branches-shifts-row-${row.id}`}
                        />
                    </div>
                ),
            },
        ],
        [i18n.language, t],
    );

    // `contents` wrapper: `DataGrid` forwards no unknown props, so a spread
    // testid never reaches the DOM. `display: contents` adds no box, so the
    // grid's layout and scoped CSS are untouched.
    return (
        <div
            className="contents"
            {...getNodeTestIdProp('branches-shifts-table')}
        >
            {isLoading ? (
                <DataGrid<{ id: string }>
                    aria-label={t('branches.shifts.tableAria')}
                    className="ledger-data-grid"
                    columns={buildDataGridSkeletonColumns(columns)}
                    contentClassName="min-w-[960px]"
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'branches-shifts',
                    )}
                    getRowId={(row) => row.id}
                />
            ) : (
                <DataGrid<ShiftRowModel>
                    aria-label={t('branches.shifts.tableAria')}
                    className="ledger-data-grid"
                    columns={columns}
                    contentClassName="min-w-[960px]"
                    data={rows}
                    getRowId={(row) => row.id}
                    onRowAction={(key) => onSelect(String(key))}
                    renderEmptyState={() => (
                        <div
                            className="flex flex-col items-center gap-3 py-12 text-muted"
                            {...getNodeTestIdProp(
                                'branches-shifts-table-empty',
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-8 text-default-500"
                                icon="gravity-ui:folder-open"
                            />
                            <span className="max-w-sm text-center text-sm">
                                {t('branches.shifts.empty')}
                            </span>
                        </div>
                    )}
                />
            )}
        </div>
    );
};
